import moment from "moment-timezone";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useContext, useEffect, useState, useCallback } from "react";
import api from "../../services/api";

/**
 * ✅ HOOK PARA MANEJO CENTRALIZADO DE ZONA HORARIA
 * 
 * Propósito:
 * - Interpretar fechas "naive" (sin zona horaria) desde el backend
 * - Convertir a la zona horaria configurada de la empresa
 * - Formatear fechas/horas para visualización
 * - Calcular diferencias de tiempo correctamente
 * 
 * Contexto:
 * - Backend guarda fechas como strings "naive": "2025-12-31 13:15:00"
 * - No tienen información de zona horaria (no UTC, no local)
 * - Deben interpretarse en la zona horaria de la empresa
 * 
 * Fuente de timezone:
 * - Settings table (key="timezone", companyId=X) vía API /settings/timezone
 * - Fallback: America/Lima
 */
export function useTimezone() {
  const { user } = useContext(AuthContext);
  const [companyTimezone, setCompanyTimezone] = useState("America/Lima"); // Default fallback

  useEffect(() => {
    let isMounted = true; // ✅ Flag para evitar memory leak
    
    const fetchCompanyTimezone = async () => {
      if (user && user.companyId) {
        try {
          const { data } = await api.get(`/settings/timezone`);
          // ✅ CORRECCIÓN CRÍTICA: Verificar si el componente sigue montado antes de actualizar estado
          if (isMounted && data && data.value) {
            setCompanyTimezone(data.value);
          }
        } catch (error) {
          // ✅ Solo loggear error si el componente sigue montado
          if (isMounted) {
            console.error("Error fetching company timezone:", error);
          }
        }
      }
    };
    
    fetchCompanyTimezone();
    
    // ✅ Cleanup: Marcar como desmontado
    return () => {
      isMounted = false;
    };
  }, [user]);

  /**
   * Obtiene la zona horaria configurada
   * Prioridad:
   * 1. Zona horaria de Settings (companyId del usuario)
   * 2. Fallback: America/Lima
   */
  const getConfiguredTimezone = useCallback(() => {
    return companyTimezone;
  }, [companyTimezone]);

  /**
   * Parsea una fecha desde el backend (puede ser naive o ISO)
   *
   * ✅ Regla práctica (consistente con nuestra config de backend):
   * - ISO con Z/offset → interpretar como UTC y convertir a timezone de la empresa
   * - Naive "YYYY-MM-DD HH:mm:ss[.SSSSSS]" → interpretar como hora LOCAL de la empresa (sin shift)
   *
   * Motivo:
   * - En backend usamos MySQL `dateStrings: true` y NO seteamos timezone en Sequelize.
   * - Por lo tanto, MySQL devuelve strings sin TZ que representan la hora local del servidor/empresa.
   */
  const parseNaiveDate = useCallback((dateInput) => {
    if (!dateInput) return null;
    
    const timezone = companyTimezone;
    let dateString = dateInput;
    
    // Si es un objeto Date, convertirlo a string
    if (dateInput instanceof Date) {
      dateString = dateInput.toISOString();
    } else if (typeof dateInput === 'string') {
      dateString = dateInput;
    } else {
      console.warn("🌍 [parseNaiveDate] Tipo de fecha no reconocido:", typeof dateInput, dateInput);
      return null;
    }
    
    // ✅ DETECTAR SI ES ISO STRING (tiene 'T' o 'Z' o termina en timezone offset)
    const isISOString = dateString.includes('T') || dateString.includes('Z') || dateString.match(/[+-]\d{2}:\d{2}$/);
    
    if (isISOString) {
      // ✅ ES ISO STRING: Convertir de UTC a zona horaria de la empresa
      const m = moment.utc(dateString);
      if (m.isValid()) {
        return m.tz(timezone);
      }
    } else {
      // ✅ ES NAIVE STRING: interpretar como hora local de la empresa
      // Formato esperado: "YYYY-MM-DD HH:mm:ss" o "YYYY-MM-DD HH:mm:ss.SSSSSS"
      const naivePattern = /^(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2}:\d{2})(\.\d+)?$/;
      const match = dateString.match(naivePattern);
      
      if (match) {
        const m = moment.tz(
          dateString,
          ['YYYY-MM-DD HH:mm:ss.SSSSSS', 'YYYY-MM-DD HH:mm:ss'],
          timezone
        );
        if (m.isValid()) return m;
      } else {
        // ✅ Fallback: intentar parseo en timezone de empresa
        const m = moment.tz(dateString, timezone);
        if (m.isValid()) return m;
        // ✅ Último fallback: intentar UTC->TZ (por si el backend manda naive en UTC)
        const m2 = moment.utc(dateString);
        if (m2.isValid()) return m2.tz(timezone);
      }
    }
    
    console.warn("Error parseando fecha:", dateString);
    return null;
  }, [companyTimezone]);

  /**
   * Formatea una fecha naive como hora (HH:mm)
   * @param {string} naiveString - Fecha en formato "YYYY-MM-DD HH:mm:ss"
   * @returns {string} - Hora formateada "HH:mm"
   */
  const formatTime = useCallback((dateInput) => {
    const m = parseNaiveDate(dateInput);
    if (!m || !m.isValid()) {
      return "";
    }
    return m.format("HH:mm");
  }, [parseNaiveDate]);

  /**
   * Formatea una fecha naive como fecha completa (DD/MM/YYYY HH:mm)
   * @param {string} naiveString - Fecha en formato "YYYY-MM-DD HH:mm:ss"
   * @returns {string} - Fecha formateada "DD/MM/YYYY HH:mm"
   */
  const formatDateTime = useCallback((naiveString) => {
    const m = parseNaiveDate(naiveString);
    if (!m || !m.isValid()) return "";
    return m.format("DD/MM/YYYY HH:mm");
  }, [parseNaiveDate]);

  /**
   * Formatea una fecha naive como fecha (DD/MM/YYYY)
   * @param {string} naiveString - Fecha en formato "YYYY-MM-DD HH:mm:ss"
   * @returns {string} - Fecha formateada "DD/MM/YYYY"
   */
  const formatDate = useCallback((naiveString) => {
    const m = parseNaiveDate(naiveString);
    if (!m || !m.isValid()) return "";
    return m.format("DD/MM/YYYY");
  }, [parseNaiveDate]);

  /**
   * Calcula "hace cuánto tiempo" desde una fecha naive
   * @param {string} naiveString - Fecha en formato "YYYY-MM-DD HH:mm:ss"
   * @returns {object} - { labelText: string, labelColor: string }
   * 
   * Retorna texto y color según la antigüedad:
   * - < 1 min: "Hace unos momentos" (verde)
   * - 1-10 min: "Hace X minutos" (verde)
   * - 10-30 min: "Hace X minutos" (amarillo)
   * - 30-60 min: "Hace X minutos" (rojo)
   * - 1-24 hrs: "Hace X horas" (rojo)
   * - 1+ días: "Hace X días" (rojo oscuro)
   */
  const getTimeAgo = useCallback((naiveString) => {
    const m = parseNaiveDate(naiveString);
    if (!m || !m.isValid()) {
      return { labelText: '', labelColor: '#6b7280' };
    }

    const timezone = companyTimezone;
    const now = moment().tz(timezone);
    
    const minutesDiff = now.diff(m, 'minutes');
    const hoursDiff = now.diff(m, 'hours');
    const daysDiff = now.diff(m, 'days');

    let labelText = '';
    let labelColor = '';

    // Formato "Hace xx minutos/horas/días"
    if (minutesDiff < 1) {
      labelText = 'Hace unos momentos';
      labelColor = '#10b981'; // verde claro
    } else if (minutesDiff === 1) {
      labelText = 'Hace 1 minuto';
      labelColor = '#10b981'; // verde claro
    } else if (minutesDiff < 60) {
      labelText = `Hace ${minutesDiff} minutos`;
      labelColor = minutesDiff <= 10 ? '#10b981' : minutesDiff < 30 ? '#f59e0b' : '#ef4444'; // verde -> amarillo -> rojo
    } else if (hoursDiff === 1) {
      labelText = 'Hace 1 hora';
      labelColor = '#ef4444'; // rojo
    } else if (hoursDiff < 24) {
      labelText = `Hace ${hoursDiff} horas`;
      labelColor = '#ef4444'; // rojo
    } else if (daysDiff === 1) {
      labelText = 'Hace 1 día';
      labelColor = '#dc2626'; // rojo oscuro
    } else {
      labelText = `Hace ${daysDiff} días`;
      labelColor = '#dc2626'; // rojo oscuro
    }

    return { labelText, labelColor };
  }, [parseNaiveDate, companyTimezone]);

  /**
   * Obtiene la hora actual en la zona horaria configurada
   * @returns {moment.Moment} - Momento actual en la zona horaria configurada
   */
  const getNow = useCallback(() => {
    const timezone = companyTimezone;
    return moment().tz(timezone);
  }, [companyTimezone]);

  return {
    parseNaiveDate,
    formatTime,
    formatDateTime,
    formatDate,
    getTimeAgo,
    getNow,
    getConfiguredTimezone
  };
}
